/**
 * Messenger REST client — thin wrapper over the Node backend's
 * /api/v1/conversations|messages|attachments endpoints.
 *
 * Style mirrors `features/presence/services/presence.api.ts`.
 *
 * All read paths return the canonical wire shapes from `messenger.wire.ts`.
 * Live updates flow through the Phoenix channel (see `messenger.socket.ts`).
 */

import type {
  Attachment,
  Conversation,
  ConversationParticipant,
  CreateConversationInput,
  MarkReadInput,
  Message,
  Paginated,
  PatchConversationInput,
  ReactionInput,
  SendMessageInput,
} from './messenger.wire'
import { trace } from './trace'
import { getAppBearer, getSdkOrAppToken } from '@/lib/sdk-session-token'

/**
 * Base URL. The messenger control-plane — conversations, history, the
 * cross-conversation activity feed (`/api/v1/messages`) — is served by the
 * global SDK server (`SDK`). (`contacts.api.ts` still uses the inMeet org
 * directory — that's identity, which the principle deliberately leaves with
 * inMeet.)
 *
 * **Attachments are the exception**: the SDK is bring-your-own-storage and
 * hosts no blobs, so uploads go to the inMeet backend (`API`) — inMeet's own
 * storage, in its role as host app — and only the resulting URL + metadata is
 * handed to the SDK inline with the message.
 *
 * `SDK` falls back to `API` when `VITE_SDK_API_URL` is unset, so a build without
 * the SDK base configured keeps working against inMeet.
 */
const API = import.meta.env.VITE_API_URL || ''
const SDK = import.meta.env.VITE_SDK_API_URL || API

/** Attachment blobs live on the inMeet backend; everything else on the SDK server. */
function isHostPath(path: string): boolean {
  return path.startsWith('/api/v1/attachments')
}

function baseFor(path: string): string {
  return isHostPath(path) ? API : SDK
}

/**
 * The right Bearer for the target server — these are two different issuers and
 * neither accepts the other's token.
 *
 * The SDK server verifies its own RS256 session token (`iss: instavc-sdk`); the
 * inMeet backend's `requireAuth` only does `verifyInternalJwt`, i.e. the HS256
 * app token. Sending the SDK token to the inMeet backend is a straight 401 —
 * which is exactly what attachment uploads did when they moved over to it.
 */
function tokenFor(path: string): Promise<string> {
  return isHostPath(path) ? Promise.resolve(getAppBearer()) : getSdkOrAppToken()
}

function getToken(): string {
  try {
    const raw =
      localStorage.getItem('inmeet_auth_session') ||
      sessionStorage.getItem('inmeet_auth_session')
    if (!raw) return ''
    const parsed = JSON.parse(raw) as { session?: { accessToken?: string; expiresAt?: number } }
    const s = parsed?.session
    if (s?.expiresAt && s.expiresAt <= Date.now()) return ''
    return s?.accessToken || ''
  } catch {
    return ''
  }
}

export class MessengerApiError extends Error {
  code: string
  status: number
  details?: Record<string, unknown>
  constructor(message: string, code: string, status: number, details?: Record<string, unknown>) {
    super(message)
    this.code = code
    this.status = status
    this.details = details
  }
}

interface SendOpts extends RequestInit {
  /** Skip parsing the response body — e.g. 204 No Content. */
  ignoreBody?: boolean
}

async function send<T>(path: string, init: SendOpts = {}): Promise<T> {
  const token = await tokenFor(path)
  const { ignoreBody, ...rest } = init
  const base = baseFor(path)
  const method = rest.method ?? 'GET'
  trace('rest', `→ ${method} ${path}  @ ${base || '(same-origin)'}`, rest.body ? safeJson(rest.body) : undefined)
  const t0 = performance.now()
  const res = await fetch(`${base}${path}`, {
    ...rest,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(rest.headers ?? {}),
    },
  })
  const ms = Math.round(performance.now() - t0)
  if (!res.ok) {
    trace('rest', `← ${res.status} ${method} ${path} (${ms}ms) ERROR`)
    const body = (await res.json().catch(() => ({}))) as {
      error?: { code?: string; message?: string; details?: Record<string, unknown> }
    }
    throw new MessengerApiError(
      body?.error?.message || `messenger api ${res.status}`,
      body?.error?.code || 'API_ERROR',
      res.status,
      body?.error?.details,
    )
  }
  if (ignoreBody || res.status === 204) {
    trace('rest', `← ${res.status} ${method} ${path} (${ms}ms) no-body`)
    return undefined as T
  }
  const json = (await res.json()) as T
  trace('rest', `← ${res.status} ${method} ${path} (${ms}ms)`, summarize(json))
  return json
}

/** Compact a response for the console (counts instead of huge arrays). */
function summarize(v: unknown): unknown {
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>
    if (Array.isArray(o.items)) return { items: `Array(${o.items.length})`, nextCursor: o.nextCursor, hasMore: o.hasMore }
    if (Array.isArray(o.presence)) return { presence: `Array(${o.presence.length})` }
    if (Array.isArray(o.contacts)) return { contacts: `Array(${o.contacts.length})` }
  }
  return v
}

function safeJson(body: BodyInit): unknown {
  try {
    return typeof body === 'string' ? JSON.parse(body) : body
  } catch {
    return body
  }
}

/** Multipart upload — strips Content-Type so the browser sets the boundary. */
async function uploadMultipart<T>(path: string, formData: FormData): Promise<T> {
  const token = await tokenFor(path)
  const res = await fetch(`${baseFor(path)}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: formData,
  })
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: { code?: string; message?: string; details?: Record<string, unknown> }
    }
    throw new MessengerApiError(
      body?.error?.message || `attachment upload ${res.status}`,
      body?.error?.code || 'API_ERROR',
      res.status,
      body?.error?.details,
    )
  }
  return res.json() as Promise<T>
}

function qs(params: Record<string, string | number | undefined>): string {
  const parts: string[] = []
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '' || v === null) continue
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
  }
  return parts.length ? `?${parts.join('&')}` : ''
}

export const messengerApi = {
  /* ===== Conversations ===== */

  /** Paginated thread list, newest first. */
  listConversations(opts: { limit?: number; before?: string } = {}): Promise<Paginated<Conversation>> {
    return send<Paginated<Conversation>>(`/api/v1/conversations${qs(opts)}`)
  },

  /**
   * Create DM (idempotent — same caller+other pair returns same conversation
   * id) or group (caller is auto-added as admin).
   */
  createConversation(input: CreateConversationInput): Promise<{ conversation: Conversation }> {
    return send<{ conversation: Conversation }>(`/api/v1/conversations`, {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },

  /** Detail + hydrated participants. */
  getConversation(id: string): Promise<{ conversation: Conversation }> {
    return send<{ conversation: Conversation }>(`/api/v1/conversations/${id}`)
  },

  /** Per-participant flags (muted/pinned/archived) + group title (admin only). */
  patchConversation(id: string, input: PatchConversationInput): Promise<{ conversation: Conversation }> {
    return send<{ conversation: Conversation }>(`/api/v1/conversations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    })
  },

  /**
   * Clear Chat — empty the thread FOR ME while keeping it in my list.
   *
   * One-sided: the other participants keep their history and are not notified.
   * `keepStarred` mirrors WhatsApp's checkbox and defaults to true server-side;
   * the messages that survive come back as `keptCount`.
   */
  clearConversation(id: string, keepStarred = true): Promise<{ clearedAt: string; keptCount: number }> {
    return send<{ clearedAt: string; keptCount: number }>(`/api/v1/conversations/${id}/clear`, {
      method: 'POST',
      body: JSON.stringify({ keepStarred }),
    })
  },

  /**
   * Delete Chat — dismiss the thread from my list AND drop its history for me.
   * Also one-sided: a new message resurfaces it carrying only what arrived
   * after this point, so the other party's chat continues uninterrupted. 204.
   */
  leaveConversation(id: string, keepStarred = true): Promise<void> {
    return send<void>(`/api/v1/conversations/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ keepStarred }),
      ignoreBody: true,
    })
  },

  /** Add members (admin only, D5 100-cap enforced). */
  addMembers(id: string, userIds: string[]): Promise<{ participants: ConversationParticipant[] }> {
    return send<{ participants: ConversationParticipant[] }>(`/api/v1/conversations/${id}/members`, {
      method: 'POST',
      body: JSON.stringify({ userIds }),
    })
  },

  /** Remove a member (admin only OR self-remove). 204. */
  removeMember(id: string, userId: string): Promise<void> {
    return send<void>(
      `/api/v1/conversations/${id}/members/${encodeURIComponent(userId)}`,
      { method: 'DELETE', ignoreBody: true },
    )
  },

  /** Promote/demote a member to admin (admin only, group only). */
  setMemberAdmin(id: string, userId: string, isAdmin: boolean): Promise<{ participants: ConversationParticipant[] }> {
    return send<{ participants: ConversationParticipant[] }>(
      `/api/v1/conversations/${id}/members/${encodeURIComponent(userId)}`,
      { method: 'PATCH', body: JSON.stringify({ isAdmin }) },
    )
  },

  /** Mark caller's read cursor. Monotonic + future-clamped server-side. */
  markRead(id: string, input: MarkReadInput): Promise<{ lastReadAt: string }> {
    return send<{ lastReadAt: string }>(`/api/v1/conversations/${id}/read`, {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },

  /* ===== Messages ===== */

  /**
   * Cross-conversation message list for the calling user. Backs the Web
   * calls-tab (W14) — pass `type: 'call'` to get a filter view over
   * `messenger.messages WHERE type='call'` across every conversation the
   * user belongs to. Cursor + ordering match `listMessages`.
   */
  listMessagesForMe(
    opts: { type?: 'text' | 'system' | 'call'; limit?: number; before?: string } = {},
  ): Promise<Paginated<Message>> {
    return send<Paginated<Message>>(`/api/v1/messages${qs(opts)}`)
  },

  /** Cursor-paginated history. Deleted messages appear w/ placeholder body. */
  listMessages(
    conversationId: string,
    opts: { limit?: number; before?: string } = {},
  ): Promise<Paginated<Message>> {
    return send<Paginated<Message>>(`/api/v1/conversations/${conversationId}/messages${qs(opts)}`)
  },

  /**
   * Send a message. `attachmentIds` link pre-uploaded orphans atomically.
   * `clientId` is accepted now; full idempotent-retry semantics land later
   * (chat O15).
   */
  sendMessage(conversationId: string, input: SendMessageInput): Promise<{ message: Message }> {
    return send<{ message: Message }>(`/api/v1/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },

  /**
   * Delete a message, for the caller alone or for everyone.
   *
   * `everyone` retracts it and is the sender's to make; `me` writes a
   * per-reader tombstone and says nothing to anybody else. Both are
   * idempotent, so a retry is not an error.
   */
  deleteMessage(messageId: string, scope: 'me' | 'everyone' = 'everyone'): Promise<{ ok: true; scope: string }> {
    return send<{ ok: true; scope: string }>(
      `/api/v1/messages/${messageId}?scope=${scope}`,
      { method: 'DELETE' },
    )
  },

  /* ===== Reactions ===== */

  /** Idempotent — re-adding the same triple returns 201 with same row. */
  addReaction(messageId: string, input: ReactionInput): Promise<{ reaction: { messageId: string; userId: string; emoji: string; createdAt: string } }> {
    return send(`/api/v1/messages/${messageId}/reactions`, {
      method: 'POST',
      body: JSON.stringify(input),
    })
  },

  /** Idempotent — removing a non-existent reaction returns 204. */
  removeReaction(messageId: string, emoji: string): Promise<void> {
    return send<void>(
      `/api/v1/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
      { method: 'DELETE', ignoreBody: true },
    )
  },

  /* ===== Attachments (inMeet's own storage — the SDK hosts no blobs) ===== */

  /**
   * Multipart upload to the inMeet backend. The browser sets the boundary.
   *
   * `purpose` tells the server which upload policy applies (an `image` slot
   * accepts images only). It is appended BEFORE the file because the server
   * reads multipart fields in order and decides on the file part as it arrives.
   *
   * `private` asks for a chat file kept private (VAPT 2026-10): its URL then
   * names the file and opens only through private-attachment.ts. A group
   * avatar or chat background is not private: it is shown to people who never
   * see a message.
   */
  uploadAttachment(
    file: File,
    opts: { private?: boolean; purpose?: 'image' } = {},
  ): Promise<{ attachment: HostAttachment }> {
    const fd = new FormData()
    if (opts.purpose) fd.append('purpose', opts.purpose)
    fd.append('file', file)
    if (opts.private) fd.append('private', 'true')
    return uploadMultipart<{ attachment: HostAttachment }>(`/api/v1/attachments`, fd)
  },

  /**
   * Resolve an uploaded blob to a fetchable URL. Local backend returns the
   * durable `/raw` proxy path (auth re-checked per request); S3 returns a
   * presigned URL.
   */
  getAttachmentUrl(attachmentId: string): Promise<{ url: string; expiresInSec: number }> {
    return send<{ url: string; expiresInSec: number }>(`/api/v1/attachments/${attachmentId}/url`)
  },

  /** Absolutize a host-backend attachment path — the SDK only accepts absolute
   *  http(s) URLs, and the local backend hands back a relative `/raw` path. */
  absoluteAttachmentUrl(url: string): string {
    if (/^https?:\/\//i.test(url)) return url
    const base = API || window.location.origin
    return new URL(url, base).toString()
  },

  /**
   * Upload a file and return a URL that is safe to store.
   *
   * One function because the three callers that did this by hand did not all
   * do it the same way: the S3 backend hands back an absolute URL, the local
   * one hands back a relative `/api/v1/attachments/:id/raw`, and a relative URL
   * stored on a conversation is meaningless to anyone who reads it from another
   * origin. Group avatars were picked, uploaded and then silently lost for
   * exactly that reason.
   */
  async uploadAndResolveUrl(file: File, purpose?: 'image'): Promise<string> {
    const { attachment } = await messengerApi.uploadAttachment(file, { purpose })
    const raw = attachment.url ?? (await messengerApi.getAttachmentUrl(attachment.id)).url
    return messengerApi.absoluteAttachmentUrl(raw)
  },
}

/** The inMeet backend's own attachment row — its storage, its shape. Mapped to
 *  the SDK's {@link Attachment} before it ever reaches `chat.send()`. */
export interface HostAttachment {
  id: string
  storageKey: string
  mime: string
  sizeBytes: number
  filename: string | null
  width: number | null
  height: number | null
  durationMs: number | null
  thumbnailKey: string | null
  url?: string
  createdAt: string
}
